import React, { useContext } from 'react';
import { CustomInput, CustomTextArea, ListDropDown } from 'components';
import { CreateEditJobNavEnum, CreateJobErrorTypes, LeadSource, LeadSourceOptions } from 'containers/jobs/constant';
import { CreateJobContextType } from 'containers/jobs/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  removeJobValidationErrors,
  selectAutoFilledData,
  selectExternalCompany,
  selectJobDescription,
  selectJobLeadSource,
  selectJobLeadSourceType,
  selectJobTitle,
  selectJobValidationErrors,
  updateExternalCompany,
  updateJobDescription,
  updateJobLeadSource,
  updateJobLeadSourceType,
  updateJobTitle
} from '../createJobSlice';
import { JobLeadSourceType } from 'containers/jobs/jobs.model';
import { getBorderStyles, getJobValidationErrors } from 'containers/jobs/utils';
import AutoFilledIndicator from './AutoFilledIndicator';
import { useGetExternalCompaniesQuery } from 'containers/settings/external-company-settings/api';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { CommissionStatus } from 'containers/settings/commission-settings/constants';
import ExternalCompanyDropdown from './ExternalCompanySection';
import { DropDownChevronDownIcon, ModalCloseIcon } from 'assets/icons';

interface JobDetailsSectionProps {
  context: React.Context<CreateJobContextType>;
}

const JobDetailsSection: React.FC<JobDetailsSectionProps> = ({ context }) => {
  const { selectedJobTab, setSelectedJobTab } = useContext<CreateJobContextType>(context);

  const selectedJobTitle = useAppSelector(selectJobTitle);
  const selectedJobDescription = useAppSelector(selectJobDescription);
  const selectedLeadSource = useAppSelector(selectJobLeadSource);
  const selectedLeadSourceType: JobLeadSourceType = useAppSelector(selectJobLeadSourceType);
  const selectedExternalCompany = useAppSelector(selectExternalCompany);
  const selectedJobValidations = useAppSelector(selectJobValidationErrors);
  const autoFilledData = useAppSelector(selectAutoFilledData);
  const { editJobSource } = useAppSelector(selectCurrentTeamJobPermissions);
  const dispatch = useAppDispatch();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const { data: companiesData } = useGetExternalCompaniesQuery(
    { teamId: currentTeam?.id?.toString() || '', status: CommissionStatus.ACTIVE },
    {
      skip: !currentTeam?.id
    }
  );

  const isTabSelected = selectedJobTab === CreateEditJobNavEnum.JOB_DETAILS;

  const showLeadInputField =
    selectedLeadSourceType?.id === LeadSource.REFERRAL || selectedLeadSourceType?.id === LeadSource.OTHER;

  const jobSourceError = getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.JOB_SOURCE);

  const hasError = Boolean(
    getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.JOB_TITLE) || jobSourceError
  );

  const onJobTitleChange = (value: string) => {
    dispatch(updateJobTitle(value));
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.JOB_TITLE));
  };

  const onJobDescriptionChange = (value: string) => {
    dispatch(updateJobDescription(value));
  };

  const onUpdateLeadSourceType = (value: JobLeadSourceType) => {
    dispatch(updateJobLeadSourceType(value));
    dispatch(updateJobLeadSource(''));
    if (value?.id && ![LeadSource.REFERRAL, LeadSource.OTHER].includes(value.id)) {
      dispatch(removeJobValidationErrors(CreateJobErrorTypes.JOB_SOURCE));
    }
  };

  const onLeadSourceChange = (value: string) => {
    dispatch(updateJobLeadSource(value));
    if (value) {
      dispatch(removeJobValidationErrors(CreateJobErrorTypes.JOB_SOURCE));
    }
  };

  const onUpdateExternalCompany = (value: { id: string; name: string } | null) => {
    dispatch(updateExternalCompany(value || null));
    if (value) {
      dispatch(removeJobValidationErrors(CreateJobErrorTypes.JOB_SOURCE));
    }
  };

  const onFocus = () => {
    setSelectedJobTab(CreateEditJobNavEnum.JOB_DETAILS);
  };

  const borderStyle = getBorderStyles({
    isSelected: isTabSelected,
    hasError
  });

  const jobTitleError = getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.JOB_TITLE);

  const jobLeadSourceTrailingIcon = (
    <div className="flex items-center gap-3">
      {selectedLeadSourceType?.id && (
        <ModalCloseIcon
          className="h-[16px] w-[16px] text-gray-400"
          aria-hidden="true"
          onClick={e => {
            e.stopPropagation();
            onUpdateLeadSourceType(null);
          }}
        />
      )}
      <DropDownChevronDownIcon className="h-[8px] w-[14px] text-gray-400" aria-hidden="true" />
    </div>
  );

  return (
    <div onFocus={onFocus}>
      <div className="mb-2 text-xl font-bold text-heading">Job details</div>
      <div className={`flex flex-col gap-6 rounded-md border ${borderStyle} px-8 py-6`}>
        <div>
          <div className="mb-2 text-17px font-semibold text-headingGray">
            Job title<span className="text-error">*</span>
            <AutoFilledIndicator isAutoFilled={autoFilledData?.title && autoFilledData?.title == selectedJobTitle} />
          </div>
          {jobTitleError && <div className="mb-2 text-sbase font-semibold text-error">{jobTitleError}</div>}
          <CustomInput
            id="create-job-title"
            onChange={onJobTitleChange}
            placeholder="Enter job title"
            value={selectedJobTitle}
            className="px-5 text-[17px] font-normal leading-5 outline-none placeholder:text-[17px]"
            maxLength={50}
          />
        </div>
        <div>
          <div className="mb-2 text-17px font-semibold text-headingGray">
            Job description{' '}
            <AutoFilledIndicator
              isAutoFilled={autoFilledData?.description && autoFilledData?.description == selectedJobDescription}
            />
          </div>
          <CustomTextArea
            id="create-job-title"
            onChange={onJobDescriptionChange}
            placeholder="Add description"
            value={selectedJobDescription}
            name="job details"
            className="h-[84px] overflow-y-auto px-5 py-3 text-[17px] font-normal leading-5
            text-heading outline-none placeholder:text-[17px]"
          />
        </div>
        {editJobSource && (
          <div>
            <div className="mb-2 text-17px font-semibold text-headingGray">
              Job source
              {/* <span className="text-error">*</span> */}
            </div>
            {/* <div className={`mt-1 text-[15px] ${jobSourceError ? 'font-semibold text-error' : 'text-[#828282]'}`}>
            At least one field required
          </div> */}
            <div className="mt-1 rounded-md border border-borderGray p-6">
              <div id="lead-source-section">
                <div className="mb-1 text-17px font-semibold text-headingGray">
                  Lead source{' '}
                  <AutoFilledIndicator
                    isAutoFilled={
                      autoFilledData?.leadSourceType && autoFilledData?.leadSourceType == selectedLeadSourceType?.id
                    }
                  />
                </div>
                <div className="flex">
                  <div className="w-1/2">
                    <ListDropDown
                      id="lead-source-dropdown"
                      items={LeadSourceOptions}
                      selectedItem={selectedLeadSourceType}
                      setSelectedItem={onUpdateLeadSourceType}
                      buttonStyles="bg-secondaryBtn h-[50px] text-[17px] font-semibold text-primaryText 
                    cursor-pointer pl-5"
                      placeholder="Choose lead source"
                      spanStyles={`font-normal tex-[17px] leading-[21px] ${
                        selectedLeadSourceType ? 'text-primaryText' : 'text-accentText opacity-70'
                      }`}
                      itemStyle="font-normal text-[17px] tracking-[-0.3px] leading-[28px]"
                      trailingIcon={jobLeadSourceTrailingIcon}
                      iconStyle="absolute right-5 pr-0 pointer-events-auto"
                    />
                  </div>
                  {showLeadInputField && (
                    <div className="ml-5 w-1/2">
                      <CustomInput
                        id="lead-source-input"
                        onChange={onLeadSourceChange}
                        placeholder={
                          selectedLeadSourceType.id === LeadSource.REFERRAL
                            ? 'Enter referrer name'
                            : 'Enter lead source'
                        }
                        value={selectedLeadSource}
                        className="mt-1 px-5 text-[17px] font-normal leading-5 outline-none placeholder:text-[17px]"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div id="external-company-section" className="mt-6">
                <div className="mb-1 text-17px font-semibold text-headingGray">
                  External company
                  <AutoFilledIndicator
                    isAutoFilled={
                      autoFilledData?.externalCompanyId &&
                      autoFilledData?.externalCompanyId == selectedExternalCompany?.id
                    }
                  />
                </div>
                <div className="flex gap-5">
                  <ExternalCompanyDropdown
                    companies={
                      companiesData?.data?.map(company => ({ id: company.id, name: company.companyName })) || []
                    }
                    selectedCompany={selectedExternalCompany}
                    onSelectCompany={onUpdateExternalCompany}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailsSection;
